import { submitBugReport } from './bugReport/submitReport';
import { formatReportId } from './bugReport/reportId';

const Prefix = 'aistart';

const PrivacyUrl = 'https://privacy.autoimmunegame.com';

/** Matches the bug report button, which has to sit above this too. */
const ZIndex = 2147482000;

export type WebGLProbe = {
	ok: boolean;
	softwareOnly?: boolean;
	renderer?: string;
	reason?: string;
};

/**
 * Same as what PIXI 5's isWebGLSupported() asks for.
 * Note PIXI never asks for webgl2, so neither do we.
 */
const PixiContextOptions: WebGLContextAttributes = {
	stencil: true,
	failIfMajorPerformanceCaveat: true,
};

/** Tries for a context, returning null rather than throwing. */
function getContext(canvas: HTMLCanvasElement, options: WebGLContextAttributes): WebGLRenderingContext | null {
	try {
		return (canvas.getContext('webgl', options)
			|| canvas.getContext('experimental-webgl', options)) as WebGLRenderingContext | null;
	} catch {
		return null;
	}
}

/** The real renderer behind a context - the masked one is always generic. */
function rendererOf(context: WebGLRenderingContext): string {
	try {
		const debugInfo = context.getExtension('WEBGL_debug_renderer_info');
		return String(
			(debugInfo && context.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL))
			|| context.getParameter(context.RENDERER)
			|| ''
		);
	} catch {
		return '';
	}
}

function release(context: WebGLRenderingContext | null) {
	// Browsers only allow a handful of live contexts and the game needs its own.
	try {
		context?.getExtension('WEBGL_lose_context')?.loseContext();
	} catch {
		// Nothing to do.
	}
}

/**
 * Decides whether the game can render, by doing the same test PIXI will do.
 */
export function probeWebGL(): WebGLProbe {
	try {
		if (!(window as any).WebGLRenderingContext) {
			return { ok: false, reason: 'This browser has no WebGL support at all (no WebGLRenderingContext).' };
		}

		// Done on a scratch canvas rather than the game's, so a failed attempt can't
		// leave the game's canvas in a half-initialised state.
		const canvas = document.createElement('canvas');

		const strict = getContext(canvas, PixiContextOptions);
		if (strict) {
			const gotStencil = strict.getContextAttributes()?.stencil === true;
			const renderer = rendererOf(strict);
			release(strict);

			if (gotStencil) return { ok: true, renderer };

			return {
				ok: false,
				renderer,
				reason: 'WebGL is available but cannot provide a stencil buffer, which the game requires.',
			};
		}

		const relaxed = getContext(canvas, { stencil: true });
		if (relaxed) {
			const renderer = rendererOf(relaxed);
			release(relaxed);
			return {
				ok: false,
				softwareOnly: true,
				renderer,
				reason: `Hardware accelerated WebGL is unavailable; only a software renderer was offered`
					+ `${renderer ? ` ("${renderer}")` : ''}, which the game's renderer refuses to run on.`,
			};
		}

		return { ok: false, reason: 'No WebGL context could be created (webgl and experimental-webgl both refused).' };
	} catch (e: any) {
		return { ok: false, reason: `Checking for WebGL threw: ${e?.message ?? e}` };
	}
}

const Styles = `
.${Prefix}-root {
	position: fixed;
	inset: 0;
	z-index: ${ZIndex};
	background: #14110d;
	color: #e3d9cb;
	font-family: 'NotoSans', 'Segoe UI', Helvetica, Arial, sans-serif;
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 24px;
	overflow-y: auto;
}
.${Prefix}-panel {
	max-width: 560px;
	font-size: 15px;
	line-height: 1.5;
}
.${Prefix}-title {
	font-size: 24px;
	font-weight: bold;
	margin: 0 0 12px 0;
}
.${Prefix}-panel p { margin: 0 0 12px 0; }
.${Prefix}-panel ol { margin: 0 0 16px 0; padding-left: 22px; }
.${Prefix}-panel li { margin-bottom: 8px; }
.${Prefix}-actions {
	display: flex;
	flex-wrap: wrap;
	gap: 10px;
	/* Enough room that the text below never reads as a caption on the button */
	margin: 14px 0 18px 0;
}

/* The default dark blue is unreadable against this panel at this size */
.${Prefix}-panel a {
	color: #8fc7f0;
}
.${Prefix}-panel a:hover {
	color: #b8dcf7;
}
.${Prefix}-btn {
	border: 2px solid #e3d9cb;
	border-radius: 6px;
	background: transparent;
	color: #e3d9cb;
	padding: 9px 16px;
	font-size: 15px;
	cursor: pointer;
	font-family: inherit;
}
.${Prefix}-btn:hover { background: #e3d9cb; color: #14110d; }
.${Prefix}-btn-primary { background: #e3d9cb; color: #14110d; }
.${Prefix}-btn-primary:hover { background: #ffffff; }
.${Prefix}-note {
	font-size: 12.5px;
	opacity: 0.75;
	margin-top: 20px;
}
.${Prefix}-status {
	margin-top: 14px;
	font-size: 13.5px;
}
.${Prefix}-status-success { color: #9fd3a0; }
.${Prefix}-status-error { color: #e79a9a; }
.${Prefix}-status-success, .${Prefix}-status-error {
	/* The report id has to be readable and copyable */
	user-select: text;
}
.${Prefix}-note code {
	font-family: 'Courier New', Courier, monospace;
	background: rgba(227, 217, 203, 0.15);
	padding: 1px 5px;
	border-radius: 3px;
}
`;

/**
 * What a browser player sees.
 */
const BrowserMessageHtml = `
	<div class="${Prefix}-panel">
		<h1 class="${Prefix}-title">WebGL Is Not Available</h1>
		<p>
			Auto Immune needs WebGL, the browser feature for hardware 3D graphics, and
			this browser isn't providing it.
		</p>
		<p>
			<strong>Check that hardware acceleration is enabled in your browser's
			settings</strong>, then reload this page. In Chrome and Edge the setting is
			under System; in Firefox it is under Performance. Some browsers also turn
			WebGL off in private or battery-saving modes.
		</p>
		<p>
			If it is already enabled, your graphics drivers may be out of date. We
			recommend getting them from AMD, Intel or NVIDIA directly.
		</p>
	</div>
`;

type ElectronGpuMode = {
	isUnsafe: boolean;
	/** The command line switch that turns unsafe mode on permanently. */
	unsafeModeSwitch?: string;
};

/** True in our Electron app, false in a browser. */
function isDesktopApp(): boolean {
	return !!(window as any).electronApi;
}

function gpuModeApi() {
	return (window as any).electronApi?.gpuMode;
}

/**
 * Replaces the game with an explanation and the three things a player can
 * usefully do. Returns nothing - once this is up, the game does not start.
 */
export async function showWebGLUnavailableScreen(probe: WebGLProbe) {
	// Kept out of the screen itself - it means nothing to a player. It still goes
	// to the console, so the captured log carries it, and into the report below.
	const detail = probe.reason ?? 'WebGL is unavailable.';
	console.error(`Cannot start: ${detail}`);

	const style = document.createElement('style');
	style.textContent = Styles;
	document.head.appendChild(style);

	const root = document.createElement('div');
	root.className = `${Prefix}-root`;

	if (!isDesktopApp()) {
		root.innerHTML = BrowserMessageHtml;
		document.body.appendChild(root);
		return;
	}

	let gpuMode: ElectronGpuMode | null = null;
	try {
		gpuMode = (await gpuModeApi()?.get()) ?? null;
	} catch {
		// The bridge is gone. The screen still works, it just can't offer the relaunch.
	}

	// Only the desktop build can relaunch itself with different GPU switches, and
	// there is no point offering it to someone already running in that mode.
	const canTryUnsafe = !!gpuModeApi()?.retryInUnsafeMode && !gpuMode?.isUnsafe;

	root.innerHTML = `
		<div class="${Prefix}-panel">
			<h1 class="${Prefix}-title">Unable to Access GPU</h1>
			<p>
				This computer is not providing safe hardware-accelerated 3D graphics,
				which we need to render this game. That is
				<strong>usually due to a graphics driver that is out of date or has been
				disabled</strong>.
			</p>
			<ol>
				<li>
					<strong>Update your graphics drivers.</strong> We recommend getting 
					them from AMD, Intel or NVIDIA directly. Try again after updating 
					and restarting your computer.
				</li>
				<li>
					<strong>Send us a report.</strong> Tells us about the issue along with details of your
					computer - graphics card, driver, processor and memory. This may be considered private information. 
					If you choose to send a report, this information goes only to us, and is extremely helpful for 
					diagnosing and fixing the issue.
					<a href="${PrivacyUrl}" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
					<div class="${Prefix}-actions ${Prefix}-actions-tight">
						<button type="button" class="${Prefix}-btn ${Prefix}-btn-primary" id="${Prefix}-report">Send Report</button>
					</div>
					<div class="${Prefix}-status" id="${Prefix}-status"></div>
				</li>
				${canTryUnsafe ? `<li>
					<strong>Try unsafe mode.</strong> Restarts the game and asks your computer
					to use its graphics hardware anyway. For some users this may work, but there 
					is a higher risk of graphics bugs and crashes.
					<div class="${Prefix}-actions">
						<button type="button" class="${Prefix}-btn" id="${Prefix}-unsafe">Try Unsafe Mode</button>
					</div>
				</li>` : ''}
			</ol>

			${gpuMode?.isUnsafe
			? `<p class="${Prefix}-note">This run already tried unsafe mode and graphics still
				aren't available. Closing and reopening the game returns it to normal.</p>`
			: ''}
			${canTryUnsafe && gpuMode?.unsafeModeSwitch
			? `<p class="${Prefix}-note">
				If unsafe mode works for you and you would rather not see this screen every
				time, add <code>${gpuMode.unsafeModeSwitch}</code> to the game's launch options:
				in Steam, right-click Auto Immune in your library, choose Properties, and put
				it in the Launch Options box. The game will then always start in unsafe mode.
			</p>`
			: ''}
			<p class="${Prefix}-note">
				If none of this helps, email autoimmunegame@gmail.com with the report ID
				you get from Send Report.
			</p>
		</div>
	`;
	document.body.appendChild(root);

	const reportButton = root.querySelector(`#${Prefix}-report`) as HTMLButtonElement | null;
	const status = root.querySelector(`#${Prefix}-status`) as HTMLElement | null;

	const setStatus = (text: string, kind?: 'error' | 'success') => {
		if (!status) return;
		status.textContent = text;
		status.className = `${Prefix}-status` + (kind ? ` ${Prefix}-status-${kind}` : '');
	};

	reportButton?.addEventListener('click', async () => {
		if (reportButton.disabled) return;
		reportButton.disabled = true;
		setStatus('Sending report…');

		const result = await submitBugReport({
			description: `[Automatic] ${probe.softwareOnly
				? 'The game cannot run usably: WebGL is software rendered only.'
				: 'The game could not start: no WebGL context available.'}\n${detail}`,
			includeSystemInfo: true,
			screenshot: null,
		});

		if (result.ok) {
			reportButton.textContent = 'Report Sent';
			setStatus(`Thank you. Your report ID is ${formatReportId(result.reportId)} - quote it if you email us.`, 'success');
		} else {
			reportButton.disabled = false;
			setStatus(result.message, 'error');
		}
	});

	root.querySelector(`#${Prefix}-unsafe`)?.addEventListener('click', () => {
		gpuModeApi()?.retryInUnsafeMode();
	});

}

/** Set synchronously, before the game bundle runs, so it can be trusted there. */
const BlockedFlag = '__autoimmuneCannotStart';

/**
 * Checks if this machine has webgl available and whether the game can run.
 * Displays a custom error screen with options for the user if it cannot.
 * Should run from the pre-flight bundle, before the game bundle is even parsed.
 */
export function runStartupChecks() {
	const probe = probeWebGL();

	if (!probe.ok) {
		(window as any)[BlockedFlag] = true;
		const show = () => { showWebGLUnavailableScreen(probe); };
		if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', show);
		else show();
		return;
	}

}

export function gameWasBlockedFromStarting(): boolean {
	return (window as any)[BlockedFlag] === true;
}
